import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { getPatRepository, createPat, updatePat, deletePat, testServerConnection, migratePatTenants } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Trash2, Pencil, Copy, Check, X, User, Server, Link2, ArrowRightLeft, AlertTriangle } from 'lucide-react';

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

// Rótulos por tipo de servidor (derivado do base_url no backend).
const TYPE_LABEL = { evolution: 'Evolution', wasender: 'WasenderAPI' };
const typeBadgeClass = (type) =>
  type === 'wasender'
    ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
    : 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300';
const keyLabelForType = (t) => (t === 'wasender' ? 'PAT' : 'API Key');

export default function PatRepositoryPage() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dialog de cadastro/edição
  const [typePickerOpen, setTypePickerOpen] = useState(false); // seletor de tipo antes de criar
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = criar; objeto = editar
  const [form, setForm] = useState({ pat: '', base_url: '', label: '', capacity: 1, server_type: 'evolution' });

  // Tipo em edição no formulário (ao criar, escolhido no passo 1; ao editar, vem do próprio servidor)
  const formType = editing ? editing.type : form.server_type;
  const isWasenderForm = formType === 'wasender';
  const formKeyLabel = keyLabelForType(formType);
  // O badge de tipo aparece nos cards e no dropdown de Admins só quando há mais de um tipo
  // cadastrado — se tudo é Evolution (como hoje), o badge vira ruído e some sozinho.
  const hasMultipleTypes = new Set(items.map((i) => i.type)).size > 1;

  // Testar conexão
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, count?} | {ok:false, error}

  // Exclusão
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Migração em massa (mover todas as contas de um servidor p/ outro)
  const [migrateFrom, setMigrateFrom] = useState(null);
  const [migrateDest, setMigrateDest] = useState('');
  const [migrating, setMigrating] = useState(false);

  // Confirmação de troca de URL em servidor com instâncias em uso
  const [confirmUrlChange, setConfirmUrlChange] = useState(false);

  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await getPatRepository();
      setItems(data.items || []);
      setSummary(data.summary || null);
    } catch (e) {
      toast.error(errMsg(e, 'Não foi possível carregar os servidores.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // "Novo Servidor" abre primeiro o seletor de tipo; escolhido o tipo, abre o modal de criação
  // já com os campos daquele tipo (assim cada modal tem a altura natural, sem toggle interno).
  const openCreate = () => setTypePickerOpen(true);

  const startCreate = (type) => {
    setEditing(null);
    setForm({ pat: '', base_url: '', label: '', capacity: 1, server_type: type });
    setTestResult(null);
    setTypePickerOpen(false);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ pat: p.pat, base_url: p.base_url || '', label: p.label, capacity: p.capacity, server_type: p.type });
    setTestResult(null);
    setDialogOpen(true);
  };

  const handleTest = async () => {
    const key = (editing ? editing.pat : form.pat).trim();
    const base = form.base_url.trim();
    if (!base || !key) {
      toast.error('Informe a URL do servidor e a API Key para testar.');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await testServerConnection({ base_url: base, api_key: key });
      setTestResult(data);
      if (data.ok) toast.success('Conexão OK.');
      else toast.error(data.error || 'Falha ao conectar.');
    } catch (e) {
      const r = { ok: false, error: errMsg(e, 'Falha ao testar.') };
      setTestResult(r);
      toast.error(r.error);
    } finally {
      setTesting(false);
    }
  };

  const stripSlash = (s) => (s || '').trim().replace(/\/+$/, '');

  const handleSave = () => {
    const capacity = parseInt(form.capacity, 10);
    if (!editing && !form.pat.trim()) { toast.error(`Informe o ${formKeyLabel}.`); return; }
    if (!isWasenderForm && !form.base_url.trim()) { toast.error('Informe a URL do servidor.'); return; }
    if (!capacity || capacity < 1) {
      toast.error('A capacidade deve ser pelo menos 1.');
      return;
    }
    // Trocar a URL de um servidor COM instâncias em uso pode desconectar as contas
    // ligadas a ele (as instâncias vivem no servidor do endereço antigo). Confirma antes.
    if (editing && (editing.used || 0) > 0 && stripSlash(form.base_url) !== stripSlash(editing.base_url)) {
      setConfirmUrlChange(true);
      return;
    }
    doSave();
  };

  const doSave = async () => {
    const capacity = parseInt(form.capacity, 10);
    setSaving(true);
    try {
      if (editing) {
        // Ao editar um WasenderAPI não mandamos base_url (não tem URL); Evolution manda a URL.
        const upd = { label: form.label.trim(), capacity };
        if (!isWasenderForm) upd.base_url = form.base_url.trim();
        await updatePat(editing.id, upd);
        toast.success('Servidor atualizado.');
      } else {
        await createPat({
          pat: form.pat.trim(),
          base_url: isWasenderForm ? '' : form.base_url.trim(),
          server_type: form.server_type,
          label: form.label.trim(),
          capacity,
        });
        toast.success('Servidor cadastrado.');
      }
      setConfirmUrlChange(false);
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Não foi possível salvar o servidor.'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (p, active) => {
    setItems((prev) => prev.map((it) => (it.id === p.id ? { ...it, active } : it)));
    try {
      await updatePat(p.id, { active });
    } catch (e) {
      toast.error(errMsg(e, 'Não foi possível alterar o status do servidor.'));
      load();
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deletePat(toDelete.id);
      toast.success('Servidor removido.');
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Não foi possível remover o servidor.'));
    } finally {
      setDeleting(false);
    }
  };

  const handleMigrate = async () => {
    if (!migrateFrom || !migrateDest) return;
    setMigrating(true);
    try {
      const { data } = await migratePatTenants(migrateFrom.id, { dest_pat_id: migrateDest });
      toast.success(`${data.migrated} conta(s) migrada(s) para ${data.dest_label}. Elas precisam re-escanear o QR.`);
      setMigrateFrom(null);
      setMigrateDest('');
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Não foi possível migrar as contas.'));
    } finally {
      setMigrating(false);
    }
  };

  const copyKey = async (p) => {
    try {
      await navigator.clipboard.writeText(p.pat);
      setCopiedId(p.id);
      setTimeout(() => setCopiedId((c) => (c === p.id ? null : c)), 1500);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  return (
    <div className="space-y-6" data-testid="pat-repository-page">
      {/* Cabeçalho + resumo — fixos no topo enquanto os cards rolam por baixo */}
      <div className="sticky -top-3 z-20 bg-background -mt-3 pt-3 pb-4 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Servidores WhatsApp</h1>
            <p className="text-muted-foreground">
              As instâncias são atribuídas automaticamente a novas contas e liberadas quando encerradas.
            </p>
          </div>
          <Button onClick={openCreate} data-testid="pat-new-button">
            <Server className="w-4 h-4 mr-2" /> Novo Servidor
          </Button>
        </div>

        {!loading && summary && (
          <Card className="shadow-sm">
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryBox label="Servidores" value={summary.total_pats} />
                <SummaryBox label="Instâncias totais" value={summary.total_capacity} />
                <SummaryBox label="Instâncias em uso" value={summary.total_used} />
                <SummaryBox label="Instâncias disponíveis" value={summary.total_free} highlight />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Lista de servidores */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Nenhum servidor cadastrado ainda. Clique em <strong>Novo Servidor</strong> para começar.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 auto-rows-fr pb-6">
        {items.map((p) => (
          <Card key={p.id} className={`h-full flex flex-col ${p.active ? '' : 'opacity-70'}`} data-testid="pat-card">
            <CardHeader className="pb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <CardTitle className="text-xl">{p.label || 'Servidor sem nome'}</CardTitle>
                  <Badge
                    variant="outline"
                    className={`gap-1 justify-center w-[7.5rem] shrink-0 border-transparent shadow ${typeBadgeClass(p.type)}`}
                  >
                    <Server className="w-3 h-3" />
                    {TYPE_LABEL[p.type]}
                  </Badge>
                  {!p.active && <Badge variant="secondary">Pausado</Badge>}
                </div>
                {/* WasenderAPI não tem URL; reserva a linha (invisível) pra a barra de
                    instâncias ficar na mesma altura dos cards que têm link. */}
                {p.base_url ? (
                  <div className="text-base text-muted-foreground break-all mt-1">{p.base_url}</div>
                ) : (
                  <div className="text-base mt-1 invisible select-none" aria-hidden="true">—</div>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground shrink-0">{keyLabelForType(p.type)}:</span>
                  <code className="text-base text-muted-foreground break-all font-mono">{p.pat}</code>
                  <button
                    type="button"
                    onClick={() => copyKey(p)}
                    className="text-muted-foreground hover:text-foreground shrink-0"
                    title={`Copiar ${keyLabelForType(p.type)}`}
                  >
                    {copiedId === p.id ? <Check className="w-3.5 h-3.5 text-teal-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              <CapacityMeter used={p.used} capacity={p.capacity} free={p.free} />
              <div className="mb-4">
                <SlotPills capacity={p.capacity} occupants={p.occupants} />
              </div>
              <div className="flex items-center gap-3 mt-auto pt-3 border-t">
                <div
                  className="flex items-center gap-2 mr-auto"
                  title="Controla se este servidor recebe NOVAS alocações. Pausar não desconecta quem já está nele, apenas para de alocar novas contas aqui."
                >
                  <Switch
                    checked={p.active}
                    onCheckedChange={(v) => handleToggleActive(p, v)}
                    aria-label="Aceitar novas alocações"
                  />
                  <span className="text-sm text-muted-foreground">{p.active ? 'Aceitar novas alocações' : 'Pausado'}</span>
                </div>
                <Button variant="outline" size="icon" onClick={() => openEdit(p)} title="Editar servidor">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => { setMigrateFrom(p); setMigrateDest(''); }}
                  disabled={(p.used || 0) === 0}
                  title={(p.used || 0) === 0 ? 'Sem contas para migrar' : 'Migrar todas as contas para outro servidor'}
                  data-testid="pat-migrate-button"
                >
                  <ArrowRightLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setToDelete(p)}
                  title={p.used > 0 ? 'Não é possível excluir: servidor em uso' : 'Excluir servidor'}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        </div>
      )}

      {/* Passo 1 — seletor de tipo (abre antes do modal de criação) */}
      <Dialog open={typePickerOpen} onOpenChange={setTypePickerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Servidor</DialogTitle>
            <DialogDescription>Qual tipo de servidor você quer cadastrar?</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <button
              type="button"
              onClick={() => startCreate('evolution')}
              className="text-left rounded-lg border p-4 transition hover:border-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20"
              data-testid="picker-evolution"
            >
              <div className="flex items-center gap-2">
                <Server className="w-4 h-4 text-cyan-600" />
                <span className="font-semibold">Evolution</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Servidor Evolution próprio (URL + API Key global). Para self-host.
              </p>
            </button>
            <button
              type="button"
              onClick={() => startCreate('wasender')}
              className="text-left rounded-lg border p-4 transition hover:border-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20"
              data-testid="picker-wasender"
            >
              <div className="flex items-center gap-2">
                <Server className="w-4 h-4 text-slate-500" />
                <span className="font-semibold">WasenderAPI</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Provedor WasenderAPI (só o PAT/token, sem URL de servidor).
              </p>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Servidor' : `Cadastrar Servidor ${TYPE_LABEL[formType]}`}</DialogTitle>
            <DialogDescription>
              {editing
                ? (isWasenderForm
                    ? `Atualize o nome e a capacidade. O ${formKeyLabel} não pode ser alterado.`
                    : 'Atualize a URL, o nome e a capacidade. A API Key não pode ser alterada.')
                : (isWasenderForm
                    ? `Informe o ${formKeyLabel} da WasenderAPI e quantas instâncias ele comporta.`
                    : 'Informe o servidor Evolution (URL + API Key global) e quantas instâncias ele comporta.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!isWasenderForm && (
              <div className="space-y-1.5">
                <Label htmlFor="base_url">URL do servidor</Label>
                <Input
                  id="base_url"
                  value={form.base_url}
                  onChange={(e) => { setForm({ ...form, base_url: e.target.value }); setTestResult(null); }}
                  placeholder="https://evolution.seudominio.com"
                  autoFocus={!editing}
                  data-testid="server-url-input"
                />
              </div>
            )}
            {!editing && (
              <div className="space-y-1.5">
                <Label htmlFor="pat">{formKeyLabel}</Label>
                <Input
                  id="pat"
                  value={form.pat}
                  onChange={(e) => { setForm({ ...form, pat: e.target.value }); setTestResult(null); }}
                  placeholder={isWasenderForm ? 'PAT (token) da WasenderAPI' : 'Global API Key do servidor Evolution'}
                  autoFocus={isWasenderForm && !editing}
                  data-testid="pat-input"
                />
              </div>
            )}
            {/* Testar conexão — só Evolution (WasenderAPI não tem URL pra testar) */}
            {!isWasenderForm && (
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline" onClick={handleTest} disabled={testing} data-testid="server-test-button">
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                Testar conexão
              </Button>
              {testResult && (
                testResult.ok ? (
                  <span className="wa-test-pop text-sm text-teal-700 dark:text-teal-400 flex items-center gap-1">
                    <style>{`
                      @keyframes wa-test-pop {
                        0%   { transform: scale(0.6); opacity: 0; }
                        60%  { transform: scale(1.1); opacity: 1; }
                        100% { transform: scale(1); }
                      }
                      .wa-test-pop { animation: wa-test-pop 0.3s cubic-bezier(0.22, 1, 0.36, 1) both; }
                      @media (prefers-reduced-motion: reduce) { .wa-test-pop { animation: none; } }
                    `}</style>
                    <Check className="w-4 h-4" /> Conectado
                  </span>
                ) : (
                  <span className="text-sm text-red-600 flex items-center gap-1">
                    <X className="w-4 h-4" /> {testResult.error}
                  </span>
                )
              )}
            </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="label">Nome</Label>
              <Input
                id="label"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Ex.: Servidor principal"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="capacity">Capacidade (instâncias)</Label>
              <Input
                id="capacity"
                type="number"
                min={editing ? editing.used || 1 : 1}
                value={form.capacity}
                onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                data-testid="pat-capacity-input"
              />
              {editing && editing.used > 0 && (
                <p className="text-xs text-muted-foreground">
                  Em uso por {editing.used} conta(s). A capacidade não pode ser menor que isso.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            {!editing && (
              <Button
                variant="outline"
                onClick={() => { setDialogOpen(false); setTypePickerOpen(true); }}
                disabled={saving}
                className="mr-auto"
              >
                Selecionar tipo de servidor
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="pat-save-button">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? 'Salvar' : 'Cadastrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de exclusão */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir servidor?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.used > 0 ? (
                <>Este servidor está em uso por <strong>{toDelete.used} conta(s)</strong>. Não é possível
                excluí-lo agora: desative-o ou aguarde a liberação das instâncias.</>
              ) : (
                <>O servidor <strong>{toDelete?.label || toDelete?.base_url || toDelete?.pat}</strong> será removido.
                Esta ação não pode ser desfeita.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deleting || (toDelete?.used > 0)}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmação de troca de URL de servidor em uso */}
      <AlertDialog open={confirmUrlChange} onOpenChange={(o) => !o && setConfirmUrlChange(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Trocar a URL deste servidor?</AlertDialogTitle>
            <AlertDialogDescription>
              Este servidor tem <strong>{editing?.used || 0} instância(s) em uso</strong>. Trocar a URL só é
              seguro se for o <strong>mesmo servidor</strong> em outro endereço. Apontar para um servidor
              diferente vai <strong>desconectar</strong> essas contas (as instâncias vivem no endereço antigo).
              Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doSave(); }} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Trocar URL
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Migração em massa de contas de um servidor para outro */}
      <Dialog open={!!migrateFrom} onOpenChange={(o) => { if (!o) { setMigrateFrom(null); setMigrateDest(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Migrar contas do servidor</DialogTitle>
            <DialogDescription>
              Todas as <strong>{migrateFrom?.used || 0} conta(s)</strong> de{' '}
              <strong>{migrateFrom?.label || migrateFrom?.pat}</strong> serão movidas para o servidor
              escolhido. Elas serão <strong>desconectadas</strong> (a instância antiga é removida) e
              precisarão re-escanear o QR no destino.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Servidor de destino</Label>
            <Select value={migrateDest} onValueChange={setMigrateDest}>
              <SelectTrigger data-testid="migrate-dest-select">
                <SelectValue placeholder="Escolha o destino" />
              </SelectTrigger>
              <SelectContent>
                {items.filter((s) => s.id !== migrateFrom?.id).map((s) => {
                  const free = (s.capacity || 0) - (s.used || 0);
                  const enough = free >= (migrateFrom?.used || 0);
                  return (
                    <SelectItem key={s.id} value={s.id} disabled={!enough}>
                      <span className="flex items-center gap-2">
                        <span>{s.label || 'Servidor sem nome'}</span>
                        <Badge variant="outline" className={`gap-1 border-transparent shadow ${typeBadgeClass(s.type)}`}>
                          <Server className="w-3 h-3" />
                          {TYPE_LABEL[s.type]}
                        </Badge>
                        <span className="text-muted-foreground">— {free} vaga(s){enough ? '' : ' (insuficiente)'}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {items.filter((s) => s.id !== migrateFrom?.id).length === 0 && (
              <p className="text-xs text-muted-foreground">Não há outro servidor cadastrado para receber as contas.</p>
            )}
            {hasMultipleTypes && migrateFrom && (() => {
              const dest = items.find((s) => s.id === migrateDest);
              const crossType = !!dest && dest.type !== migrateFrom.type;
              // Sempre no layout p/ o modal não pular; texto do tipo "oposto" só reserva a altura quando não há aviso real.
              const destType = crossType ? dest.type : (migrateFrom.type === 'wasender' ? 'evolution' : 'wasender');
              return (
                <div
                  className={`flex items-center gap-2 rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-950/20 p-2.5 text-xs text-amber-800 dark:text-amber-300 ${crossType ? '' : 'invisible'}`}
                  aria-hidden={!crossType}
                >
                  <AlertTriangle className="w-5 h-5 shrink-0" />
                  <span>
                    Você está movendo de <strong>{TYPE_LABEL[migrateFrom.type]}</strong> para{' '}
                    <strong>{TYPE_LABEL[destType]}</strong>. O comportamento do provedor muda, confirme se é intencional.
                  </span>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMigrateFrom(null); setMigrateDest(''); }} disabled={migrating}>
              Cancelar
            </Button>
            <Button onClick={handleMigrate} disabled={migrating || !migrateDest} data-testid="migrate-confirm-button">
              {migrating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
              Migrar {migrateFrom?.used || 0} conta(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryBox({ label, value, highlight }) {
  return (
    <div className={`rounded-lg p-3 text-center ${highlight ? 'bg-teal-50 dark:bg-teal-900/20' : 'bg-secondary/50'}`}>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="text-sm font-bold text-muted-foreground">{label}</p>
    </div>
  );
}

// Medidor de ocupação do servidor: legenda (em uso / livres) + barra de capacidade que
// "esquenta" (verde → vermelho) conforme enche — mesmo matiz 140→0 dos SlotPills.
function CapacityMeter({ used, capacity, free }) {
  const ratio = capacity > 0 ? Math.min(used / capacity, 1) : 0;
  const pct = Math.round(ratio * 100);
  // Severidade em DEGRAUS (não em rotação contínua de matiz): estado honesto e
  // consistente com os selos do sistema. <70% ok (teal), 70–89% atenção (âmbar),
  // ≥90% crítico (vermelho).
  const fill =
    ratio >= 0.9
      ? 'bg-gradient-to-r from-red-400 to-red-600'
      : ratio >= 0.7
        ? 'bg-gradient-to-r from-amber-400 to-amber-500'
        : 'bg-gradient-to-r from-teal-400 to-teal-600';
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-muted-foreground">Instâncias</span>
        <span className="tabular-nums font-medium">
          {used}/{capacity}
          <span className="text-muted-foreground"> · {free} {free === 1 ? 'livre' : 'livres'}</span>
        </span>
      </div>
      {/* Trilho cinza-escuro (pedido do dono): a parte vazia lê como "espaço a preencher". */}
      <div
        className="h-2 w-full rounded-full bg-gray-300 dark:bg-gray-700 overflow-hidden"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={capacity}
        aria-valuenow={used}
        aria-label={`Instâncias em uso: ${used} de ${capacity}`}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-500 ease-out ${fill}`}
          style={{ width: `${pct}%`, minWidth: used > 0 ? '8px' : 0 }}
        />
      </div>
    </div>
  );
}

// Iniciais (1ª + última palavra) para o avatar da instância ocupada.
function initials(name) {
  if (!name || name === '—') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}

// Cor da instância por posição (0-based) num único matiz que "gira" de verde → amarelo →
// laranja → vermelho. Saturação/luminosidade fixas — mesma "família" pastel; só a
// temperatura esquenta conforme o servidor enche.
const SLOT_STEPS = 9; // 10 posições: i de 0 a 9
function slotHeatStyle(i) {
  const t = Math.min(i, SLOT_STEPS) / SLOT_STEPS; // 0 → 1
  const hue = 140 - 140 * t;                      // 140 (verde) → 0 (vermelho)
  return {
    backgroundColor: `hsl(${hue}, 68%, 82%)`,
    color: `hsl(${hue}, 55%, 28%)`,
  };
}

// Instâncias como avatares: ocupadas = quadrado colorido com iniciais (clicável → conta);
// livres = quadrado tracejado com ícone de usuário.
function SlotPills({ capacity, occupants }) {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {Array.from({ length: capacity }).map((_, i) => {
        const occ = occupants[i];
        if (occ) {
          const heat = slotHeatStyle(i);
          return (
            <Popover key={i}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  style={heat}
                  className="h-10 w-14 rounded-xl text-xs font-semibold flex items-center justify-center shadow-sm ring-1 ring-black/5 hover:scale-105 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400 transition"
                >
                  {initials(occ.name)}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72">
                <div className="flex items-center gap-3">
                  <div style={heat} className="h-10 w-10 shrink-0 rounded-xl text-sm font-semibold flex items-center justify-center">
                    {initials(occ.name)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{occ.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{occ.email}</p>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3 break-all">Tenant: {occ.tenant_id}</p>
              </PopoverContent>
            </Popover>
          );
        }
        return (
          <div
            key={i}
            className="h-10 w-14 rounded-xl border-2 border-dashed border-muted-foreground/25 flex items-center justify-center text-muted-foreground/40"
          >
            <User className="w-4 h-4" />
          </div>
        );
      })}
    </div>
  );
}
